import Box, { type BoxProps } from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import { useMemo } from 'react';

import { useInView } from '@/hooks/useInView';

import { type RevealDirection, revealHiddenTransform, revealTransition } from './revealMotion';

/**
 * Past this many children the delay stops growing: a 13th card arriving a
 * second after the 1st reads as a broken page, not as choreography.
 */
const MAX_STAGGERED = 12;

type Props = BoxProps & {
  /** Milliseconds between one child and the next. */
  step?: number;
  direction?: RevealDirection;
  /** Turn the effect off without unmounting — used while a list is loading. */
  disabled?: boolean;
};

/**
 * Reveals its direct children one after another as the group scrolls into
 * view (§52).
 *
 * One `IntersectionObserver` watches the whole group rather than one per
 * child, and the sequence is pure CSS `transition-delay` on `nth-of-type`, so
 * a twelve-card grid costs exactly as much as a single `Reveal`. Reduced
 * motion, above-the-fold content and the no-JavaScript render all skip
 * straight to the visible state, so nothing is ever hidden from a visitor who
 * cannot see it arrive.
 */
export function RevealGroup({ step = 70, direction = 'up', disabled = false, sx, ...rest }: Props) {
  const { ref, state } = useInView();
  const active = !disabled && state !== 'static';

  const stagger = useMemo<SxProps<Theme>>(() => {
    const rules: Record<string, object> = {
      '& > *': { transition: revealTransition, willChange: 'opacity, transform' },
    };
    for (let i = 0; i < MAX_STAGGERED; i += 1) {
      rules[`& > *:nth-of-type(${i + 1})`] = { transitionDelay: `${i * step}ms` };
    }
    return rules;
  }, [step]);

  return (
    <Box
      ref={ref}
      sx={[
        active && stagger,
        active &&
          state === 'waiting' && {
            '& > *': { opacity: 0, transform: revealHiddenTransform[direction] },
          },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    />
  );
}
